import React, { cloneElement, isValidElement, ReactNode } from 'react';
import { isStyledComponent } from 'styled-components';

import { isNumber } from '@xemplo/common-util';
import { SvgStroke } from '@xemplo/style-constants';

import { InputSize } from '../xemplo-input.types';

/**
 * Text Input spec, leading/trailing icons should change based on Icon Size
 * Lighter for Compact size
 * Regular for Default and Spacious
 *
 */
export function getInputIconStrokeWidth(inputSize?: InputSize): number {
  return inputSize === InputSize.Compact ? SvgStroke.Light : SvgStroke.Regular;
}

/**
 * Only plain function components can be treated as icons. Host elements
 * (span/div), styled-components (e.g. a phone prefix, a button wrapping an
 * icon), and class components would leak the cloned strokeWidth prop into
 * the DOM as an invalid attribute instead of consuming it.
 */
function isIconLikeElement(type: unknown): boolean {
  if (typeof type !== 'function') return false;
  if (isStyledComponent(type)) return false;
  if (type.prototype?.isReactComponent) return false;
  return true;
}

/**
 * Applies the size-appropriate stroke weight to a leading/trailing input
 * icon. An explicitly provided strokeWidth always wins. Icons nested inside
 * a wrapper element (button, stepper container, etc.) are not reachable from
 * here — the wrapper is skipped by isIconLikeElement, and those consumers
 * must set strokeWidth on the inner icons themselves.
 */
export function applyInputIconStrokeWidth(
  icon: ReactNode,
  inputSize?: InputSize,
): ReactNode {
  if (!isValidElement<{ strokeWidth?: string | number }>(icon)) return icon;
  if (icon.props.strokeWidth !== undefined) return icon;
  if (!isIconLikeElement(icon.type)) return icon;

  return cloneElement(icon, { strokeWidth: getInputIconStrokeWidth(inputSize) });
}

export function parseCssSize(width?: string | number): string {
  if (width == null) return '100%';
  return isNumber(width) ? `${width}px` : width;
}

/**
 * @deprecated Use parseCssSizeVar instead
 * @see {@link parseCssSizeVar}
 */
export function parseWidthCssVar(width?: string | number) {
  const parsed = parseCssSize(width);
  return { '--input-width': parsed } as React.CSSProperties;
}

export function normalizeCssKey(key: string): string {
  return key.startsWith('--') ? key : `--${key}`;
}

/**
 * Tiny helper to deal with css size variables.
 * If the value is not provided, it defaults to '100%'.
 *
 * It should be used to allow simpler implementation of numbers
 * this is not designed to be a catch all for css parsing.
 *
 * NOTE: Key will be normalized and allows key with or without '--' prefix.
 *
 * @param key The key for a size css variable. e.g.: --input-width
 * @param value
 * @returns A React CSSProperties object with the parsed css variable.
 */
export function parseCssSizeVar(
  key: string,
  value?: string | number,
): React.CSSProperties {
  const normalizedKey = normalizeCssKey(key);
  const parsed = parseCssSize(value);
  return { [normalizedKey]: parsed } as React.CSSProperties;
}
